import styled from '@emotion/styled';
import { useId } from 'react';
import { colors, fonts, radii, breakpoints } from './tokens';
import { IconButton } from './IconButton';
import { PlusIcon } from './icons';

// Form controls for test screens (added for the test-screen redesign, S3):
//   Slider     - Figma "Set Contrast (52.0%)": marks 0/25/50/75/100 and -/+ icon buttons
//   Keypad     - Figma Colour Vision numeric keypad: 1-9, [C] red clear, 0, [n] teal "nothing seen"
//   RadioGroup - Figma radios with the teal filled dot (Automatic Resizing Mode On/Off, Show/Hide)

// ---------------------------------------------------------------------------------------------
// Slider
// ---------------------------------------------------------------------------------------------

const SliderRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  font-family: ${fonts.family};
`;

const SliderLabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.slate[600]};
  padding-left: ${({ indent }) => (indent ? 52 : 0)}px;
  @media (max-width: ${breakpoints.phone - 1}px) {
    font-size: 13px;
    padding-left: ${({ indent }) => (indent ? 44 : 0)}px;
  }
`;

const SliderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  @media (max-width: ${breakpoints.phone - 1}px) {
    gap: 8px;
  }
`;

const TrackBox = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  padding-top: 8px;
`;

const Range = styled.input`
  --pct: 0%;
  -webkit-appearance: none;
  appearance: none;
  display: block;
  width: 100%;
  height: 20px;
  margin: 0;
  background: transparent;
  cursor: pointer;
  position: relative;
  z-index: 2;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  &::-webkit-slider-runnable-track {
    height: 6px;
    border-radius: 3px;
    background: linear-gradient(to right, ${colors.teal} 0 var(--pct), ${colors.slate[200]} var(--pct) 100%);
  }
  &::-moz-range-track {
    height: 6px;
    border-radius: 3px;
    background: ${colors.slate[200]};
  }
  &::-moz-range-progress {
    height: 6px;
    border-radius: 3px;
    background: ${colors.teal};
  }
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    margin-top: -6px;
    border-radius: 50%;
    background: #fff;
    border: 3px solid ${colors.teal};
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
  }
  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    border: 3px solid ${colors.teal};
  }
  &:focus-visible {
    outline: 3px solid ${colors.brand[300]};
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

const Marks = styled.div`
  position: relative;
  height: 18px;
  margin: 2px 9px 0;
  font-size: 11px;
  color: ${colors.slate[400]};
  span {
    position: absolute;
    top: 0;
    transform: translateX(-50%);
    white-space: nowrap;
  }
  i {
    position: absolute;
    top: -15px;
    width: 5px;
    height: 5px;
    margin-left: -2.5px;
    border-radius: 50%;
    background: ${colors.slate[800]};
    z-index: 3;
    pointer-events: none;
  }
  i.off {
    background: ${colors.slate[300]};
  }
`;

const StepButton = styled(IconButton)`
  margin-top: 4px;
`;

const MinusGlyph = () => (
  <svg width={16} height={16} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2.5} strokeLinecap='round' aria-hidden='true'>
    <path d='M5 12h14' />
  </svg>
);

/**
 * Slider - range input with marks and optional -/+ step buttons.
 * @param value, onChange(number)   controlled value
 * @param min, max, step            range (defaults 0..100, step 0.1)
 * @param marks                     tick labels under the track (default 0/25/50/75/100; [] to hide)
 * @param label                     heading above the track, e.g. "Set Contrast (52.0%)"
 * @param stepBy                    amount for the -/+ buttons (default 1; null hides them)
 * @param formatMark                label formatter for the marks
 */
export const Slider = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 0.1,
  marks = [0, 25, 50, 75, 100],
  label,
  stepBy = 1,
  formatMark = (m) => m,
  disabled,
  ariaLabel,
  ...rest
}) => {
  const id = useId();
  const clamp = (v) => Math.min(max, Math.max(min, v));
  const pct = max === min ? 0 : ((clamp(Number(value) || 0) - min) / (max - min)) * 100;
  const emit = (v) => onChange?.(clamp(Math.round(v * 1000) / 1000));
  const showSteps = stepBy !== null && stepBy !== undefined;
  return (
    <SliderRoot {...rest}>
      {label && (
        <SliderLabel as='label' htmlFor={id} indent={showSteps}>
          {label}
        </SliderLabel>
      )}
      <SliderRow>
        {showSteps && (
          <StepButton label='Decrease' variant='outline' size={36} disabled={disabled || value <= min} onClick={() => emit(Number(value) - stepBy)}>
            <MinusGlyph />
          </StepButton>
        )}
        <TrackBox>
          <Range
            id={id}
            type='range'
            min={min}
            max={max}
            step={step}
            value={value}
            disabled={disabled}
            aria-label={label ? undefined : ariaLabel || 'Value'}
            style={{ '--pct': `${pct}%` }}
            onChange={(e) => emit(Number(e.target.value))}
          />
          {marks?.length > 0 && (
            <Marks aria-hidden='true'>
              {marks.map((m) => {
                const left = ((m - min) / (max - min)) * 100;
                return (
                  <span key={m} style={{ left: `${left}%` }}>
                    {formatMark(m)}
                  </span>
                );
              })}
              {marks.map((m) => {
                const left = ((m - min) / (max - min)) * 100;
                return <i key={`dot-${m}`} className={m > value ? 'off' : ''} style={{ left: `${left}%` }} />;
              })}
            </Marks>
          )}
        </TrackBox>
        {showSteps && (
          <StepButton label='Increase' variant='teal' size={36} style={{ borderRadius: radii.button }} disabled={disabled || value >= max} onClick={() => emit(Number(value) + stepBy)}>
            <PlusIcon size={16} strokeWidth={2.5} />
          </StepButton>
        )}
      </SliderRow>
    </SliderRoot>
  );
};

// ---------------------------------------------------------------------------------------------
// Keypad
// ---------------------------------------------------------------------------------------------

const KeypadRoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  width: 100%;
  max-width: ${({ maxWidth }) => maxWidth}px;
  font-family: ${fonts.family};
`;

const KeypadTitle = styled.div`
  text-align: center;
  font-size: 20px;
  font-weight: 700;
  color: ${colors.slate[600]};
  @media (max-width: ${breakpoints.phone - 1}px) {
    font-size: 16px;
  }
`;

const KeypadDisplay = styled.output`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  padding: 0 12px;
  border: 1.5px solid ${colors.primary};
  border-radius: 2px;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${colors.slate[700]};
  background: #fff;
`;

const Keys = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
`;

const Key = styled.button`
  aspect-ratio: 1;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${radii.button}px;
  font-family: ${fonts.family};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 100ms ease, border-color 100ms ease;
  ${({ tone }) =>
    ({
      digit: `background:#fff;color:${colors.slate[700]};border:1px solid ${colors.slate[200]};
        &:hover:not(:disabled){background:${colors.slate[50]};border-color:${colors.slate[300]};}
        &:active:not(:disabled){background:${colors.slate[100]};}`,
      clear: `background:${colors.error[500]};color:#fff;border:1px solid ${colors.error[500]};
        &:hover:not(:disabled){background:${colors.error[600]};}
        &:active:not(:disabled){background:${colors.error[700]};}`,
      none: `background:${colors.primary};color:#fff;border:1px solid ${colors.primary};
        &:hover:not(:disabled){background:${colors.primaryHover};}
        &:active:not(:disabled){background:${colors.primaryPressed};}`,
    })[tone]}
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 3px solid ${colors.brand[300]};
    outline-offset: 2px;
  }
  @media (max-width: ${breakpoints.phone - 1}px) {
    font-size: 16px;
  }
`;

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

/**
 * Keypad - numeric keypad (Colour Vision / Ishihara).
 * @param onKey(key)   key is '0'..'9', 'clear' ([C]) or 'none' ([n] = nothing seen)
 * @param title        heading above the display (e.g. "Current Characters"); omit to hide
 * @param value        text in the display box; pass undefined to hide the display
 * @param disabled     disables every key
 * @param clearLabel / noneLabel   key captions (default "C" / "n")
 * @param maxWidth     px (default 180)
 */
export const Keypad = ({ onKey, title, value, disabled, clearLabel = 'C', noneLabel = 'n', maxWidth = 180, ...rest }) => (
  <KeypadRoot maxWidth={maxWidth} {...rest}>
    {title && <KeypadTitle>{title}</KeypadTitle>}
    {value !== undefined && (
      <KeypadDisplay aria-live='polite' aria-label={title || 'Current value'} data-testid='keypad-display'>
        {value || ' '}
      </KeypadDisplay>
    )}
    <Keys role='group' aria-label='Keypad'>
      {DIGITS.map((d) => (
        <Key key={d} type='button' tone='digit' disabled={disabled} onClick={() => onKey?.(d)}>
          {d}
        </Key>
      ))}
      <Key type='button' tone='clear' disabled={disabled} aria-label='Clear' title='Clear' onClick={() => onKey?.('clear')}>
        {clearLabel}
      </Key>
      <Key type='button' tone='digit' disabled={disabled} onClick={() => onKey?.('0')}>
        0
      </Key>
      <Key type='button' tone='none' disabled={disabled} aria-label='Nothing seen' title='Nothing seen' onClick={() => onKey?.('none')}>
        {noneLabel}
      </Key>
    </Keys>
  </KeypadRoot>
);

// ---------------------------------------------------------------------------------------------
// RadioGroup
// ---------------------------------------------------------------------------------------------

const RadioRoot = styled.div`
  display: flex;
  flex-direction: ${({ direction }) => (direction === 'column' ? 'column' : 'row')};
  flex-wrap: wrap;
  gap: ${({ direction }) => (direction === 'column' ? '10px' : '8px 20px')};
  font-family: ${fonts.family};
`;

const RadioLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  font-size: 14px;
  color: ${colors.slate[600]};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.55 : 1)};
  input {
    -webkit-appearance: none;
    appearance: none;
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin: 0;
    border-radius: 50%;
    border: 1.5px solid ${colors.slate[400]};
    background: #fff;
    display: grid;
    place-content: center;
    cursor: inherit;
  }
  input::before {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${colors.teal};
    transform: scale(0);
    transition: transform 100ms ease;
  }
  input:checked {
    border-color: ${colors.teal};
  }
  input:checked::before {
    transform: scale(1);
  }
  input:focus-visible {
    outline: 3px solid ${colors.brand[300]};
    outline-offset: 2px;
  }
`;

/**
 * RadioGroup - teal-dot radios.
 * @param options   [{ value, label, disabled }] or strings
 * @param value, onChange(value)
 * @param name      input name (auto-generated when omitted)
 * @param direction 'row' (default) | 'column'
 * @param disabled  disables the whole group
 * Values are compared with ===, or by JSON when both sides are objects.
 */
export const RadioGroup = ({ options, value, onChange, name, direction = 'row', disabled, ariaLabel, ...rest }) => {
  const auto = useId();
  const groupName = name || `radio-${auto}`;
  const same = (a, b) => a === b || (a && b && typeof a === 'object' && typeof b === 'object' && JSON.stringify(a) === JSON.stringify(b));
  return (
    <RadioRoot role='radiogroup' aria-label={ariaLabel} direction={direction} {...rest}>
      {options.map((o, i) => {
        const opt = typeof o === 'string' ? { value: o, label: o } : o;
        const off = disabled || opt.disabled;
        return (
          <RadioLabel key={opt.id || (typeof opt.value === 'object' ? i : String(opt.value))} disabled={off}>
            <input type='radio' name={groupName} checked={same(opt.value, value)} disabled={off} onChange={() => onChange?.(opt.value)} />
            {opt.label}
          </RadioLabel>
        );
      })}
    </RadioRoot>
  );
};

export default Slider;
